import React, { useState } from "react";
import { Zap, Crown, Trophy, Check, ChevronRight } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useUsage } from "../../contexts/UsageContext";
import { Button } from "../ui/button";
import { QuarterlyPlanModal } from "../modals/QuarterlyPlanModal";

interface PricingSectionProps {
  onStartClipping: () => void;
  onUpgrade?: (plan: string, isYearly: boolean, isQuarterly?: boolean) => void;
  isYearly: boolean;
  setIsYearly: (value: boolean) => void;
}

export const PricingSection: React.FC<PricingSectionProps> = ({
  onStartClipping,
  onUpgrade,
  isYearly,
  setIsYearly,
}) => {
  const { user } = useAuth();
  const { usage } = useUsage();
  const [showQuarterlyModal, setShowQuarterlyModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);

  // Helper function to determine if this is the user's current plan
  const isCurrentPlan = (planName: string) => {
    if (!usage?.plan) return false;

    // Normalize plan names for comparison
    const currentPlan = usage.plan.toLowerCase();
    const checkPlan = planName.toLowerCase();

    // Handle plan name mappings
    if (currentPlan === "pro" && checkPlan === "starter") return true;
    if (currentPlan === "creator" && checkPlan === "expert") return true;

    return currentPlan === checkPlan;
  };

  // Helper function to determine if user can upgrade to this plan
  const canUpgradeTo = (planName: string) => {
    if (!usage?.plan) return true; // User has no plan, can upgrade to any

    const currentPlan = usage.plan.toLowerCase();
    const targetPlan = planName.toLowerCase();

    // Define plan hierarchy
    const planHierarchy = ["hobby", "starter", "expert"];
    const currentIndex = planHierarchy.indexOf(
      currentPlan === "pro"
        ? "starter"
        : currentPlan === "creator"
        ? "expert"
        : currentPlan
    );
    const targetIndex = planHierarchy.indexOf(
      targetPlan === "starter"
        ? "starter"
        : targetPlan === "expert"
        ? "expert"
        : targetPlan
    );

    return targetIndex > currentIndex;
  };

  const pricingPlans = [
    {
      name: "Hobby",
      icon: <Zap className="w-5 h-5" />,
      monthlyPrice: 19,
      yearlyPrice: 9,
      quarterlyPrice: 14,
      quarterlyTotal: 42,
      sourceMinutes: 100,
      yearlyTotal: 108,
      features: [
        "100 source-video minutes",
        "Vertical reframing and captions",
        "Ready-to-download clips",
      ],
    },
    {
      name: "Starter",
      icon: <Crown className="w-5 h-5" />,
      monthlyPrice: 39,
      yearlyPrice: 19,
      quarterlyPrice: 29,
      quarterlyTotal: 87,
      sourceMinutes: 200,
      yearlyTotal: 228,
      isPopular: true,
      features: [
        "200 source-video minutes",
        "Vertical reframing and captions",
        "Ready-to-download clips",
      ],
    },
    {
      name: "Expert",
      icon: <Trophy className="w-5 h-5" />,
      monthlyPrice: 69,
      yearlyPrice: 34,
      quarterlyPrice: 52,
      quarterlyTotal: 156,
      sourceMinutes: 350,
      yearlyTotal: 408,
      features: [
        "350 source-video minutes",
        "Vertical reframing and captions",
        "Ready-to-download clips",
      ],
    },
  ];

  return (
    <div className="mx-auto max-w-6xl px-5 py-8 sm:px-6 sm:py-12">
      {/* Title and Subtitle */}
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="tc-section-title mb-4 text-gray-900">
          <span className="sm:hidden">Pricing</span>
          <span className="hidden sm:inline">Simple, transparent pricing</span>
        </h2>
        <p className="mx-auto mb-2 max-w-3xl text-balance text-center text-base leading-relaxed text-gray-600 sm:text-[20px] sm:tracking-wide sm:text-[#525866]">
          Start creating viral clips today. No hidden fees. Cancel anytime.
        </p>
      </div>
      {/* Toggle */}
      <div className="flex justify-center mb-6 sm:mb-8">
        <div className="inline-flex h-10 sm:h-12 items-center justify-center rounded-lg bg-gray-100/50 p-1 mx-4 sm:mx-6">
          <button
            type="button"
            onClick={() => setIsYearly(false)}
            className={`inline-flex items-center justify-center rounded-md px-3 sm:px-6 py-1.5 text-xs sm:text-sm font-medium transition-all ${
              !isYearly
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setIsYearly(true)}
            className={`inline-flex items-center justify-center rounded-md px-3 sm:px-6 py-1.5 text-xs sm:text-sm font-medium transition-all ${
              isYearly
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <span className="whitespace-nowrap text-center">
              Yearly{" "}
              <span className="text-xs italic text-gray-500 hidden sm:inline">
                (Save 50%!)
              </span>
              <span className="text-xs italic text-gray-500 sm:hidden">
                (Save 50%)
              </span>
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div
        data-pricing-grid
        className="mb-8 grid grid-cols-1 gap-5 pt-0 sm:mb-12 md:grid-cols-3 md:gap-3 md:pt-12 lg:gap-5"
      >
        {pricingPlans.map((plan) => (
          <div
            key={plan.name}
            data-pricing-plan={plan.name}
            className="relative flex w-full justify-center"
          >
            {plan.isPopular && (
              <div className="absolute -top-1 h-[50px] w-full max-w-md rounded-t-2xl bg-[#335CFF] py-2.5 md:-top-9">
                <p className="text-center text-sm font-semibold text-white">
                  Most popular
                </p>
              </div>
            )}

            {/* Card */}
            <div
              className={`relative flex w-full max-w-md flex-col items-center justify-center space-y-6 rounded-xl border bg-white px-6 pb-7 pt-8 shadow-lg md:max-w-none md:px-4 lg:px-7 ${
                plan.isPopular
                  ? "mt-8 border-[#335CFF] shadow-[#DFE7FF] md:mt-0"
                  : "border-gray-100"
              }`}
            >
              {/* Header */}
              <div className="flex w-full flex-col items-start space-y-3">
                <div>
                  <div className="w-fit rounded-full border border-[#E5E7ED] bg-white p-3">
                    <div style={{ fill: "url(#gradient)" }}>{plan.icon}</div>
                    <svg width="0" height="0">
                      <defs>
                        <linearGradient
                          id="gradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="0%"
                        >
                          <stop
                            offset="40%"
                            style={{
                              stopColor: "rgb(130, 182, 255)",
                              stopOpacity: 1,
                            }}
                          />
                          <stop
                            offset="100%"
                            style={{
                              stopColor: "rgb(0, 13, 73)",
                              stopOpacity: 1,
                            }}
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <p className="text-lg sm:text-[20px] font-semibold text-[#101828] mt-3">
                    {plan.name}
                  </p>
                </div>

                {/* Pricing */}
                <div>
                  <p className="text-3xl font-bold text-[#101828] sm:text-4xl md:text-[1.85rem] lg:text-5xl">
                    ${isYearly ? plan.yearlyPrice : plan.monthlyPrice}/mo
                  </p>
                  <p className="text-sm sm:text-base text-[#475467]/90 mt-1">
                    {plan.sourceMinutes} credits per month
                  </p>
                </div>

                {/* Features */}
                <div className="flex w-full flex-col space-y-3 pt-3">
                  <div className="flex flex-col space-y-3">
                    {plan.features.map((feature, featureIndex) => {
                      return (
                        <div
                          key={featureIndex}
                          className="flex flex-row items-center justify-start space-x-2"
                        >
                          <div
                            className="flex items-center justify-center rounded-full p-1"
                            style={{ backgroundColor: "#D1FADF" }}
                          >
                            <Check
                              className="w-3.5 h-3.5 text-[#12B76A]"
                              strokeWidth={3}
                            />
                          </div>
                          <p className="text-sm leading-5 text-[#475467] lg:text-base">
                            {feature}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Button */}
              <Button
                onClick={() => {
                  if (isCurrentPlan(plan.name)) {
                    // Current plan - do nothing or redirect to billing
                    return;
                  } else if (user && onUpgrade && canUpgradeTo(plan.name)) {
                    // User is logged in and can upgrade to this plan
                    if (!isYearly) {
                      // Show quarterly popup for monthly selection
                      setSelectedPlan(plan);
                      setShowQuarterlyModal(true);
                    } else {
                      // Direct upgrade for yearly
                      onUpgrade(plan.name.toLowerCase(), isYearly);
                    }
                  } else if (!user) {
                    // User not logged in, go to auth flow
                    if (!isYearly) {
                      // Show quarterly popup for monthly selection
                      setSelectedPlan(plan);
                      setShowQuarterlyModal(true);
                    } else {
                      onStartClipping();
                    }
                  }
                }}
                disabled={
                  isCurrentPlan(plan.name) ||
                  Boolean(user && !canUpgradeTo(plan.name) && usage?.plan)
                }
                className={`flex min-h-[48px] w-full items-center justify-center rounded-full border px-3 py-3 transition-colors duration-200 ease-in-out sm:min-h-[52px] md:px-2 lg:px-6 ${
                  isCurrentPlan(plan.name)
                    ? "border-blue-300 bg-blue-100 text-blue-700 cursor-default"
                    : user && !canUpgradeTo(plan.name) && usage?.plan
                    ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                    : plan.isPopular
                    ? "border-gray-200 bg-[#3870FF] text-white hover:bg-[#3870FF]/90 cursor-pointer"
                    : "border-gray-200 text-[#1A191A]/70 hover:bg-gray-50 cursor-pointer"
                }`}
              >
                <p
                  className={`flex items-center justify-center gap-1 text-center text-sm font-medium lg:text-base ${
                    isCurrentPlan(plan.name)
                      ? "text-blue-700"
                      : user && !canUpgradeTo(plan.name) && usage?.plan
                      ? "text-gray-400"
                      : plan.isPopular
                      ? "text-white"
                      : "text-[#1A191A]/70"
                  }`}
                >
                  {isCurrentPlan(plan.name)
                    ? "Current Plan"
                    : !user
                    ? `Choose ${plan.name}`
                    : canUpgradeTo(plan.name)
                    ? `Choose ${plan.name}`
                    : `Choose ${plan.name}`}
                  {!isCurrentPlan(plan.name) && (
                    <ChevronRight className="ml-1 h-4 w-4" strokeWidth={3} />
                  )}
                </p>
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Quarterly Plan Modal */}
      {selectedPlan && (
        <QuarterlyPlanModal
          isOpen={showQuarterlyModal}
          onClose={() => {
            setShowQuarterlyModal(false);
            setSelectedPlan(null);
          }}
          selectedPlan={selectedPlan}
          onSelectMonthly={() => {
            setShowQuarterlyModal(false);
            if (user && onUpgrade && canUpgradeTo(selectedPlan.name)) {
              onUpgrade(selectedPlan.name.toLowerCase(), false);
            } else if (!user) {
              onStartClipping();
            }
            setSelectedPlan(null);
          }}
          onSelectQuarterly={() => {
            setShowQuarterlyModal(false);
            if (user && onUpgrade && canUpgradeTo(selectedPlan.name)) {
              onUpgrade(selectedPlan.name.toLowerCase(), false, true);
            } else if (!user) {
              onStartClipping();
            }
            setSelectedPlan(null);
          }}
        />
      )}
    </div>
  );
};
